import { handleAxiosError } from '@/api/helpers';
import { client } from '@/client';
import { PlannerCRUDCourse } from '@/client/planner/types';
import { ExcludeFormValue, TargetFormValue } from '@/common/types';
import { useAppSelector } from '@/hooks/store';
import { useToast } from '@/hooks/useToast';
import { selectCurrentAccount } from '@/store/features/account';
import { AppIconTooltip } from '@/ui/icon-tooltip';
import { FlexContainer } from '@/ui/styled-ui';
import { singularOrPluralTranslation } from '@/utils/helpers';
import { getTargetsAndExcludes } from '@/utils/targets';
import { AxiosError } from 'axios';
import { t } from 'i18next';
import { InputSwitch } from 'primereact/inputswitch';
import { OverlayPanel } from 'primereact/overlaypanel';
import { ProgressSpinner } from 'primereact/progressspinner';
import { ScrollPanel } from 'primereact/scrollpanel';
import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

const StyledPanelTrigger = styled.a`
  display: flex;
  align-items: center;
  background-color: var(--white-main);
  border-radius: var(--xsmall-border-radius);
  border: 1px solid var(--gray-dark);
  width: 100%;
  margin-top: var(--xsmall-padding);

  padding: 14px var(--small-padding);
  line-height: var(--xsmall-line-height);
  text-decoration: none;
  color: var(--black-main);

  svg {
    color: var(--gray-darker);
  }

  &.active {
    border-bottom-left-radius: 0;
    border-bottom-right-radius: 0;
    border-bottom: 0;
  }

  &.green {
    svg {
      color: var(--green-main);
    }
  }

  &.disabled {
    opacity: 0.6;
    pointer-events: none;
  }
`;

const StyledPanelOverlay = styled(OverlayPanel)`
  width: 160px;
  margin: 0;
  border-top-left-radius: 0;
  border-top-right-radius: 0;
  box-shadow: none;
  border: 1px solid var(--gray-dark);
  border-top: 0;
  padding-top: var(--xsmall-padding);

  > div {
    padding: 0;
    box-shadow: none;
    color: var(--black-main);
    position: relative;
  }
`;

const StyledSettingsContainer = styled.div`
  width: 160px;
  padding: var(--small-padding);
  padding-top: 0;

  .actions-container {
    padding-bottom: var(--xsmall-padding);

    span {
      font-size: var(--xxsmall-font-size);
      line-height: 1.2;

      &.italic {
        font-style: italic;
      }
    }
  }

  .actions {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: var(--xsmall-padding);
    font-size: var(--xxsmall-font-size);
  }

  .info {
    border-top: 1px solid var(--gray-dark);
    padding-top: var(--xsmall-padding);
    font-size: var(--xxsmall-font-size);
    padding-right: 8px;
  }

  i {
    position: absolute;
    right: 8px;
    bottom: 14px;
    font-size: var(--small-font-size);
    color: var(--black-main);
  }
`;

type PlannerSettingsProps = {
  item: PlannerCRUDCourse;
  targets: TargetFormValue[];
  excludes: ExcludeFormValue[];
  isDisabled: boolean;
  scrollPanelRef: React.RefObject<ScrollPanel>;
  onItemSkipUsersWithinGracePeriodToggle: (
    skipUsersWithinGracePeriod: boolean,
    item: PlannerCRUDCourse,
  ) => void;
  isOnboardingPlan?: boolean;
};

export const PlannerSettings: React.FC<PlannerSettingsProps> = ({
  item,
  targets,
  excludes,
  isDisabled,
  scrollPanelRef,
  onItemSkipUsersWithinGracePeriodToggle,
  isOnboardingPlan,
}) => {
  const toast = useToast();
  const opRef = useRef<OverlayPanel | null>(null);
  const currentAccount = useAppSelector(selectCurrentAccount);

  const [active, setActive] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [enrolledUsersCount, setEnrolledUsersCount] = useState<number>(0);
  const [totalUsersCount, setTotalUsersCount] = useState<number>(0);
  const [reEnrollableUsersCount, setReEnrollableUsersCount] =
    useState<number>(0);

  const getTranslatedUserCount = (count: number, areAllUsers?: boolean) =>
    singularOrPluralTranslation(
      count,
      t('generic.user.count', { count }),
      t(areAllUsers ? 'generic.all.users.count' : 'generic.users.count', {
        count,
      }),
    );

  const getEnrollCount = (
    item: PlannerCRUDCourse,
    totalUsersCount: number,
    enrolledUsersCount: number,
    reEnrollableUsersCount: number,
  ): number => {
    if (!item.skipUsersEnrolledWithinGracePeriod) {
      return totalUsersCount;
    }

    if (enrolledUsersCount === 0 && reEnrollableUsersCount === 0) {
      return totalUsersCount;
    }

    return totalUsersCount - enrolledUsersCount + reEnrollableUsersCount;
  };

  const getSkippedCount = (
    item: PlannerCRUDCourse,
    enrolledUsersCount: number,
    reEnrollableUsersCount: number,
  ): number => {
    if (!item.skipUsersEnrolledWithinGracePeriod) {
      return 0;
    }

    if (enrolledUsersCount === 0 && reEnrollableUsersCount === 0) {
      return 0;
    }

    return enrolledUsersCount - reEnrollableUsersCount;
  };

  const toggleItem = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();

    opRef?.current?.toggle(e);
  };

  const fetchCourseUsersSummary = async () => {
    if (!currentAccount?.id || !item.enrollDate || isDisabled) return;

    try {
      setIsLoading(true);
      const response = await client.courses.getCourseUsersSummary(
        {
          ...getTargetsAndExcludes(targets, excludes),
          enrollDate:
            typeof item.enrollDate === 'string'
              ? item.enrollDate
              : item.enrollDate?.toISOString(),
        },
        item.id,
        currentAccount.id,
      );

      setEnrolledUsersCount(response.enrolledUsersCount);
      setTotalUsersCount(response.totalUsersCount);
      setReEnrollableUsersCount(response.reEnrollableUsersCount);
    } catch (e) {
      handleAxiosError(e as Error | AxiosError, toast);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchCourseUsersSummary();
  }, [targets, excludes]);

  useEffect(() => {
    const slider = scrollPanelRef?.current?.getElement();
    const draggableElement = slider?.querySelector('.p-scrollpanel-content');

    // Close settings popover on timeline scroll
    if (draggableElement) {
      draggableElement.addEventListener('scroll', () => {
        opRef?.current?.hide();
      });
    }
  }, [scrollPanelRef, item]);

  return (
    <>
      <StyledPanelTrigger
        href="#"
        className={`p-panelmenu-header-link
          ${active ? 'active' : ''}
          ${item.skipUsersEnrolledWithinGracePeriod ? 'green' : ''}
          ${isDisabled ? 'disabled' : ''}
        `}
        onClick={toggleItem}
      >
        <span className="font-normal">{t('planner.settings')}</span>
        {!isDisabled && (
          <span className="flex ml-auto">
            <i
              className={`pi  p-panelmenu-icon ${
                active ? 'pi-angle-up' : 'pi-angle-down'
              }`}
            />
          </span>
        )}
      </StyledPanelTrigger>

      <StyledPanelOverlay
        ref={opRef}
        onShow={() => setActive(true)}
        onHide={() => setActive(false)}
      >
        <StyledSettingsContainer>
          {!isLoading ? (
            <>
              <div className="actions-container">
                <div className="actions">
                  <span>{t('planner.retrain.label')}</span>
                  <InputSwitch
                    className="v2 small"
                    name="skipUsersEnrolledWithinGracePeriod"
                    checked={item.skipUsersEnrolledWithinGracePeriod}
                    onChange={() => {
                      onItemSkipUsersWithinGracePeriodToggle(
                        !item.skipUsersEnrolledWithinGracePeriod,
                        item,
                      );
                    }}
                  />
                </div>
                <span
                  className="italic"
                  dangerouslySetInnerHTML={{
                    __html:
                      currentAccount?.courseReTrainGracePeriodDays === 1
                        ? t('planner.retrain.label.suffix.singular')
                        : t('planner.retrain.label.suffix', {
                            count:
                              currentAccount?.courseReTrainGracePeriodDays || 0,
                          }),
                  }}
                />
              </div>

              {!isOnboardingPlan && (
                <div
                  className="info"
                  dangerouslySetInnerHTML={{
                    __html: t('planner.users.enroll.and.skipped.count', {
                      enrollCount: getTranslatedUserCount(
                        getEnrollCount(
                          item,
                          totalUsersCount,
                          enrolledUsersCount,
                          reEnrollableUsersCount,
                        ),
                      ),
                      skippedCount: getTranslatedUserCount(
                        getSkippedCount(
                          item,
                          enrolledUsersCount,
                          reEnrollableUsersCount,
                        ),
                      ),
                    }),
                  }}
                />
              )}
              <FlexContainer
                className="tooltip"
                align="flex-end"
                justify="flex-end"
              >
                <AppIconTooltip
                  target="enroll-info"
                  content={
                    item.skipUsersEnrolledWithinGracePeriod
                      ? t('planner.retrain.on.info.tooltip', {
                          count:
                            currentAccount?.courseReTrainGracePeriodDays || 0,
                        })
                      : t('planner.retrain.off.info.tooltip')
                  }
                />
              </FlexContainer>
            </>
          ) : (
            <FlexContainer height="120px">
              <ProgressSpinner style={{ width: '24px', height: '24px' }} />
            </FlexContainer>
          )}
        </StyledSettingsContainer>
      </StyledPanelOverlay>
    </>
  );
};
